import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Music2, ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Banner after a successful email verification (?verified=1) or failed
  // verify link (?verified=0&reason=invalid_or_expired).
  const verifiedFlag = params.get("verified");
  const verifyReason = params.get("reason");

  useEffect(() => {
    if (verifiedFlag === "1") {
      toast.success("Email verified — sign in to get started.");
    } else if (verifiedFlag === "0") {
      toast.error(
        verifyReason === "invalid_or_expired"
          ? "That link has expired or already been used. Sign in to resend it."
          : "Could not verify the link. Try again or resend it.",
      );
    }
    // Strip the query string so a refresh doesn't re-trigger the toast.
    if (verifiedFlag != null) {
      window.history.replaceState({}, "", "/login");
    }
  }, [verifiedFlag, verifyReason]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      const next = location.state?.from?.pathname || "/home";
      navigate(next, { replace: true });
    } else if (res.needsVerification) {
      // Route to the "Check your email" screen instead of toasting an error.
      navigate("/check-email", {
        replace: true,
        state: { email: res.email || email },
      });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-ivory grid place-items-center p-4">
      <div className="w-full max-w-md card-base p-6 sm:p-8 animate-fade-up" data-testid="login-card">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <span className="w-9 h-9 rounded-xl bg-sage-500 text-white grid place-items-center"><Music2 size={20} /></span>
          <span className="font-heading font-semibold text-lg">AMDS</span>
        </Link>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Welcome back</h1>
        <p className="text-ink-600 text-sm mt-1">Sign in to manage your diary and bookings.</p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label className="label-overline block mb-2">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="input-field" placeholder="you@example.com"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="label-overline block mb-2">Password</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="input-field" placeholder="••••••••"
              data-testid="login-password-input"
            />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="login-submit-btn">
            {busy ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
          </button>
        </form>
        <div className="mt-6 flex items-center justify-between text-sm">
          <Link to="/forgot-password" className="text-sage-600 hover:text-sage-700" data-testid="login-forgot-link">Forgot password?</Link>
          <Link to="/register" className="text-sage-600 hover:text-sage-700" data-testid="login-signup-link">Create account</Link>
        </div>
      </div>
    </div>
  );
}
